import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle, IndianRupee, Wallet, X } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useSupervisorDashboardData } from './useSupervisorDashboardData';
import { Select } from '@/components/ui/Select';

const RENT_STATUS_COLORS: Record<string, string> = {
  PENDING:  'bg-amber-100 text-amber-800',
  PAID:     'bg-green-100 text-green-800',
  PARTIAL:  'bg-blue-100 text-blue-800',
  OVERDUE:  'bg-red-100 text-red-700',
  WAIVED:   'bg-gray-100 text-gray-600',
};

function fmt(n: number | null | undefined) {
  const v = isNaN(Number(n)) ? 0 : (Number(n) ?? 0);
  return '₹' + v.toLocaleString('en-IN');
}

type RentFilter = 'COLLECT' | 'PAID' | 'UNPAID' | 'REMAINING';
type ComplaintFilter = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED';

export function SupervisorAnalyticsPage() {
  const { t } = useTranslation();

  const RENT_POPUP_TITLE: Record<RentFilter, string> = {
    COLLECT:   t('dashboard.rentToCollect'),
    PAID:      t('dashboard.tenantsPaid'),
    UNPAID:    t('dashboard.tenantsUnpaid'),
    REMAINING: t('dashboard.remainingBalance'),
  };

  const {
    hostels, isLoading,
    activeHostels, multiHostel, selectedId, setSelectedId, isAll, activeHostelId,
    s, billableRent, totalToCollect, paidAmount, remaining, paidEntries, unpaidEntries,
    openComplaints, inProgressComplaints, resolvedComplaints,
  } = useSupervisorDashboardData();

  const [rentPopup, setRentPopup] = useState<RentFilter | null>(null);
  const rentPopupList =
    rentPopup === 'PAID' ? paidEntries :
    rentPopup === 'UNPAID' ? unpaidEntries :
    rentPopup === 'REMAINING' ? unpaidEntries :
    rentPopup === 'COLLECT' ? billableRent : [];

  const [complaintPopup, setComplaintPopup] = useState<ComplaintFilter | null>(null);
  const complaintPopupList =
    complaintPopup === 'OPEN'        ? openComplaints :
    complaintPopup === 'IN_PROGRESS' ? inProgressComplaints :
    complaintPopup === 'RESOLVED'    ? resolvedComplaints : [];

  const occupancyData = [
    { name: t('dashboard.occupiedBeds'), value: s.occupiedBeds, color: '#ef4444' },
    { name: t('dashboard.vacantBeds'), value: s.vacantBeds, color: '#22c55e' },
  ];

  const tenantStatusData = [
    { name: t('common.active'), value: s.activeTenants, fill: '#8b5cf6' },
    { name: t('common.reserved'), value: s.reservedBeds, fill: '#f59e0b' },
    { name: t('common.checkedOut'), value: s.checkedOutTenants, fill: '#6b7280' },
    { name: t('common.cancelled'), value: s.cancelledTenants, fill: '#ef4444' },
  ];

  if (!isLoading && hostels.length === 0) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('dashboard.detailedMetrics')}</h1>
        <p className="text-sm text-gray-500 mb-6">{t('dashboard.detailedMetricsSubtitle')}</p>
        <div className="bg-white rounded-xl p-10 text-center border border-dashed border-gray-200">
          <p className="text-gray-500 font-medium">{t('dashboard.noHostelsAssigned')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.detailedMetrics')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('dashboard.detailedMetricsSubtitle')}</p>
        </div>
        {multiHostel && (
          <Select
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            options={[
              { value: 'ALL', label: t('dashboard.allHostels') },
              ...activeHostels.map(h => ({ value: h.id, label: h.name })),
            ]}
          />
        )}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Occupancy donut */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.bedOccupancy')}</h3>
          <div className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={occupancyData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {occupancyData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={30} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tenant status bar chart */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.tenantStatus')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tenantStatusData}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {tenantStatusData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Rental status — clickable, opens list popup */}
      {(activeHostelId || isAll) && (
        <>
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">{t('dashboard.rentalStatus')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-8">
            <button
              type="button"
              onClick={() => setRentPopup('COLLECT')}
              className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-violet-300 hover:shadow-md transition group"
            >
              <div className="w-6 h-6 rounded-md bg-violet-50 flex items-center justify-center mb-1 group-hover:bg-violet-100 transition">
                <IndianRupee size={12} className="text-violet-600" />
              </div>
              <p className="text-sm font-bold text-gray-900 leading-none">{fmt(totalToCollect)}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{t('dashboard.toCollect')}</p>
            </button>
            <button
              type="button"
              onClick={() => setRentPopup('PAID')}
              className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-green-300 hover:shadow-md transition group"
            >
              <div className="w-6 h-6 rounded-md bg-green-50 flex items-center justify-center mb-1 group-hover:bg-green-100 transition">
                <CheckCircle size={12} className="text-green-600" />
              </div>
              <p className="text-sm font-bold text-gray-900 leading-none">{fmt(paidAmount)}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{t('rent.paid')}</p>
            </button>
            <button
              type="button"
              onClick={() => setRentPopup('UNPAID')}
              className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-red-300 hover:shadow-md transition group"
            >
              <div className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center mb-1 group-hover:bg-red-100 transition">
                <AlertTriangle size={12} className="text-red-600" />
              </div>
              <p className="text-sm font-bold text-gray-900 leading-none">{unpaidEntries.length}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{t('dashboard.unpaid')}</p>
            </button>
            <button
              type="button"
              onClick={() => setRentPopup('REMAINING')}
              className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-amber-300 hover:shadow-md transition group"
            >
              <div className="w-6 h-6 rounded-md bg-amber-50 flex items-center justify-center mb-1 group-hover:bg-amber-100 transition">
                <Wallet size={12} className="text-amber-600" />
              </div>
              <p className="text-sm font-bold text-gray-900 leading-none">{fmt(remaining)}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{t('dashboard.remaining')}</p>
            </button>
          </div>
        </>
      )}

      {rentPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setRentPopup(null)}>
          <div className="bg-chrome-page rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">{RENT_POPUP_TITLE[rentPopup]} <span className="text-sm font-normal text-gray-400">({rentPopupList.length})</span></h2>
              <button onClick={() => setRentPopup(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              {rentPopupList.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">{t('common.noTenantsInCategory')}</p>
              ) : (
                <div className="space-y-2">
                  {rentPopupList.map(e => {
                    const due = Number(e.amountDue) - Number(e.amountPaid);
                    return (
                      <Link
                        key={e.id}
                        to="/supervisor/rent"
                        search={{ hostelId: '', status: '', from: '', to: '' }}
                        onClick={() => setRentPopup(null)}
                        className="bg-gray-50 hover:bg-gray-100 rounded-xl p-3 flex items-center justify-between transition"
                      >
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{e.tenantName}</p>
                          <p className="text-xs text-gray-500">{e.tenantPhone}{e.roomNumber ? ` · Room ${e.roomNumber}` : ''}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-gray-900">{fmt(Number(e.amountPaid))} / {fmt(Number(e.amountDue))}</p>
                          {due > 0 ? (
                            <p className="text-xs text-red-600 font-medium">{fmt(due)} {t('dashboard.dueClickCollect')}</p>
                          ) : (
                            <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${RENT_STATUS_COLORS[e.status]}`}>{e.status}</span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Complaint Status */}
      {(activeHostelId || isAll) && (
        <>
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">{t('dashboard.complaintStatus')}</h2>
          <div className="grid grid-cols-3 gap-1.5 mb-8">
            <button
              type="button"
              onClick={() => setComplaintPopup('OPEN')}
              className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-red-300 hover:shadow-md transition group"
            >
              <div className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center mb-1 group-hover:bg-red-100 transition">
                <AlertTriangle size={12} className="text-red-600" />
              </div>
              <p className="text-sm font-bold text-gray-900 leading-none">{openComplaints.length}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{t('complaint.open')}</p>
            </button>
            <button
              type="button"
              onClick={() => setComplaintPopup('IN_PROGRESS')}
              className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-amber-300 hover:shadow-md transition group"
            >
              <div className="w-6 h-6 rounded-md bg-amber-50 flex items-center justify-center mb-1 group-hover:bg-amber-100 transition">
                <IndianRupee size={12} className="text-amber-600" />
              </div>
              <p className="text-sm font-bold text-gray-900 leading-none">{inProgressComplaints.length}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{t('complaint.inProgress')}</p>
            </button>
            <button
              type="button"
              onClick={() => setComplaintPopup('RESOLVED')}
              className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-green-300 hover:shadow-md transition group"
            >
              <div className="w-6 h-6 rounded-md bg-green-50 flex items-center justify-center mb-1 group-hover:bg-green-100 transition">
                <CheckCircle size={12} className="text-green-600" />
              </div>
              <p className="text-sm font-bold text-gray-900 leading-none">{resolvedComplaints.length}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{t('complaint.resolved')}</p>
            </button>
          </div>
        </>
      )}

      {complaintPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setComplaintPopup(null)}>
          <div className="bg-chrome-page rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {complaintPopup === 'OPEN' ? t('dashboard.openComplaints') : complaintPopup === 'IN_PROGRESS' ? t('complaint.inProgress') : t('dashboard.resolvedClosed')}
                <span className="text-sm font-normal text-gray-400 ml-2">({complaintPopupList.length})</span>
              </h2>
              <button onClick={() => setComplaintPopup(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button>
            </div>
            <div className="p-5">
              {complaintPopupList.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">{t('common.noComplaintsInCategory')}</p>
              ) : (
                <div className="space-y-2">
                  {complaintPopupList.map(c => (
                    <Link
                      key={c.id}
                      to="/supervisor/hostels/$id/complaints"
                      params={{ id: c.hostelId }}
                      onClick={() => setComplaintPopup(null)}
                      className="bg-gray-50 hover:bg-gray-100 rounded-xl p-3 flex items-start justify-between gap-3 transition"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm">{c.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {c.tenantName ?? 'Tenant'} · {c.category.replace('_', ' ')}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs text-violet-600 font-medium">{t('common.view')} →</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
